import { Form } from 'antd';
import type { FormInstance, FormRule } from 'antd';
import { useMemo, useState } from 'react';
import type { ZodType } from 'zod';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE zod → ANTD FORM BRIDGE.
 *
 *  PAMS_CONVENTIONS §6: "Frontend form rules derive from the shared schema. No
 *  hand-typed AntD rules. The legacy had 45 hand-written rules contradicting 20 server
 *  schemas." Those contradictions are the worst kind of bug — the form accepts what the
 *  server rejects, or rejects what the server would have accepted, and both look like
 *  the API being broken.
 *
 *  This is short because of one fact worth knowing: zod 4 issue paths are
 *  `(string | number)[]` and rc-field-form's `InternalNamePath` is ALSO
 *  `(string | number)[]`. They are structurally identical, so nested and array paths
 *  need no translation at all — `['postings', 0, 'orgUnitId']` is simultaneously a valid
 *  zod path and a valid `Form.List` name.
 *
 *  ⚠️ [amended P3a] THAT CLAIM IS NOW EXERCISED RATHER THAN ASSERTED. `Form.List` had no
 *  consumer until this pass, and the structural identity above turned out to be true for
 *  `zodRule`, `zodSubmit` and `applyServerIssues` and FALSE for `isFieldRequired`, which
 *  probed with `{}` and therefore answered for top-level fields only. See `requiredProbe`.
 *
 *  [amended P3a] An earlier version of this header claimed "ESLint blocks inline
 *  `rules={[...]}` literals in components and pages". IT NEVER DID — `web/eslint.config.js`
 *  restricts hex literals, `dangerouslySetInnerHTML`, `localStorage.setItem` in `lib/` and
 *  the dayjs/antd-picker imports, and nothing else. The convention is followed everywhere
 *  regardless; the sentence is removed rather than the rule added, because a lint rule
 *  nobody has seen fail is not evidence of anything.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type FieldPath = readonly (string | number)[];

const toPath = (name: FieldPath | string): FieldPath => (typeof name === 'string' ? [name] : name);

/** Compares a zod issue path against a field path, element by element. */
const pathStartsWith = (issuePath: readonly (string | number | symbol)[], fieldPath: FieldPath): boolean =>
  fieldPath.every((segment, index) => String(issuePath[index]) === String(segment));

/**
 * One rule per `Form.Item`, derived from the WHOLE schema.
 *
 * Returns the `RuleRender` arm — a function of the form instance — so the validator can
 * read every field, not just its own. That matters for any cross-field rule: a schema
 * `.refine()` that compares two fields is evaluated correctly here without the bridge
 * knowing anything about it.
 *
 * Not `async`: `@typescript-eslint/require-await` is an error in this repo, and an
 * async validator with no await would fail lint. Explicit promises instead.
 */
export const zodRule =
  <T>(schema: ZodType<T>, name: FieldPath | string): FormRule =>
  // `form` is contextually typed by `FormRule`'s RuleRender arm — deliberately not
  // annotated, because antd's `FormInstance` and the rc-field-form one this receives are
  // NOT the same type (antd's adds scrollToField/focusField/getFieldInstance).
  (form) => ({
    validator: (): Promise<void> => {
      const path = toPath(name);
      const result = schema.safeParse(form.getFieldsValue(true));
      if (result.success) return Promise.resolve();

      const messages = result.error.issues
        // `path.length > 0` keeps WHOLE-OBJECT refines off every individual field — they
        // surface in the form-level alert instead. Without this, "at least one field must
        // be provided" would appear under every input at once.
        .filter((issue) => issue.path.length > 0 && pathStartsWith(issue.path, path))
        .map((issue) => issue.message);

      return messages.length === 0 ? Promise.resolve() : Promise.reject(new Error(messages.join(' ')));
    },
  });

/**
 * The object `isFieldRequired` parses to ask its question.
 *
 * ⚠️ [amended P3a] NOT `{}`, WHICH ONLY EVER ANSWERED FOR TOP-LEVEL FIELDS.
 *
 * Parsing `{}` reports ONE issue per missing branch, at the OUTERMOST missing key. For
 * `['sources', 0, 'orgUnitId']` that issue lands on `['sources']`, which
 * `pathStartsWith` correctly declines to match against the longer field path — so every
 * field inside an object and every field inside a `Form.List` read as NOT required and
 * silently lost its asterisk, while its validation kept working. The two disagreed,
 * which is the exact failure this function exists to prevent.
 *
 * Materialising the containers along the path — `{ sources: [{}] }` — moves the issue
 * onto the field itself. The LAST segment is deliberately left missing, because whether
 * the schema complains about its absence is the whole question.
 */
const requiredProbe = (path: FieldPath): Record<string, unknown> => {
  const root: Record<string, unknown> = {};
  let container: Record<string, unknown> | unknown[] = root;

  for (let index = 0; index < path.length - 1; index += 1) {
    // A NUMBER as the next segment means this container is indexed, so it is an array.
    // That is the same structural identity the header relies on: a zod path and an
    // rc-field-form `InternalNamePath` are the same shape, so one walk serves both.
    const child: Record<string, unknown> | unknown[] = typeof path[index + 1] === 'number' ? [] : {};
    if (Array.isArray(container)) container[Number(path[index])] = child;
    else container[String(path[index])] = child;
    container = child;
  }

  return root;
};

/**
 * Whether a field is required, read from the schema so the asterisk cannot disagree with
 * the validation.
 *
 * Deliberately does NOT emit `{ required: true }` as a RULE — AntD would then render its
 * own generic message alongside zod's. `Form.Item`'s `required` prop with no matching
 * rule is asterisk-only, which is exactly what is wanted.
 */
export const isFieldRequired = <T>(schema: ZodType<T>, name: FieldPath | string): boolean => {
  const path = toPath(name);
  // One parse answers the question — and it ASKS THE SCHEMA rather than walking its
  // internals, which keeps this working across zod's own refactors of `.shape`,
  // `.unwrap()` and friends.
  const result = schema.safeParse(requiredProbe(path));
  if (result.success) return false;

  return result.error.issues.some(
    (issue) => issue.path.length > 0 && pathStartsWith(issue.path, path) && issue.code === 'invalid_type',
  );
};

export type SubmitResult<T> = { ok: true; data: T } | { ok: false; formErrors: string[] };

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P4-UX] A COLOUR IS NOT A MESSAGE.
 *
 *  Submitting an empty officer form turned every border red and said NOTHING. The cause is
 *  one line of routing in `zodSubmit`: an issue with a non-empty `path` goes to
 *  `form.setFields`, which paints the field; only a WHOLE-OBJECT refine (`path.length === 0`)
 *  reaches `formErrors`, which is what the eight drawers render as an `Alert`. Required
 *  fields always have a path, so the Alert was always empty for exactly the case that needed
 *  it most.
 *
 *  ⚠️ Colour-only error signalling is also an accessibility defect. This is the half of it
 *  that belongs to this pass — a sentence at the top naming the fields. The
 *  icon-and-text-per-field half is P4-UX.2's.
 *
 *  The sentence is built HERE rather than in the eight call sites, all of which already
 *  render `formErrors.join(' ')`, so none of them changes.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export type FieldLabels = Readonly<Record<string, string>>;

/**
 * `orgUnitId` → "Unit", `serviceNumber` → "Service number".
 *
 * Derived rather than read from `Form.Item label=` because rc-field-form exposes no
 * name→label map, and reading the DOM from `lib/` to find one would be worse than a
 * humanised path. Where the humanised form reads badly — `paDate` → "Pa date" — the caller
 * passes an override.
 */
const humanise = (path: FieldPath): string => {
  const last = [...path].reverse().find((segment) => typeof segment === 'string');
  const words = String(last ?? 'value')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/\bid$/, '')
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const nameFor = (path: FieldPath, labels: FieldLabels | undefined): string => {
  const key = path.filter((segment) => typeof segment === 'string').join('.');
  const last = String([...path].reverse().find((segment) => typeof segment === 'string') ?? '');
  return labels?.[key] ?? labels?.[last] ?? humanise(path);
};

/** The one sentence. Plain language, and it names the fields rather than counting them. */
export const fieldSummary = (paths: readonly FieldPath[], labels?: FieldLabels): string => {
  const names = [...new Set(paths.map((path) => nameFor(path, labels)))];
  const [first] = names;
  if (names.length === 0) return '';
  if (names.length === 1) return `${String(first)} needs attention — the message is under the field.`;
  return `${String(names.length)} fields need attention: ${names.join(', ')}.`;
};

/**
 * The authoritative submit gate.
 *
 * `Form.onFinish` hands you the form's own view of its values. This re-parses the raw
 * object through the schema, so what goes to the API is the ZOD-PARSED OUTPUT — trimmed,
 * defaulted, unknown keys stripped, and (for a phone) already normalised to ten digits.
 * Sending the raw form state instead is how a UI-normalised value and a server-normalised
 * value end up differing.
 */
export const zodSubmit =
  <T>(schema: ZodType<T>, form: FormInstance, labels?: FieldLabels) =>
  (raw: unknown): SubmitResult<T> => {
    const result = schema.safeParse(raw);
    if (result.success) return { ok: true, data: result.data };

    const formErrors: string[] = [];
    const fieldErrors = new Map<string, { name: FieldPath; errors: string[] }>();

    for (const issue of result.error.issues) {
      if (issue.path.length === 0) {
        formErrors.push(issue.message);
        continue;
      }
      const path = issue.path.map((segment) => (typeof segment === 'number' ? segment : String(segment)));
      const key = path.join('.');
      const entry = fieldErrors.get(key) ?? { name: path, errors: [] };
      entry.errors.push(issue.message);
      fieldErrors.set(key, entry);
    }

    if (fieldErrors.size > 0) {
      const entries = [...fieldErrors.values()];
      form.setFields(entries);
      const first = entries[0];
      if (first) form.scrollToField(first.name);
      /*
       * ⚠️ FIRST, so it reads before any whole-object refine. The red borders are already
       * on; this is the sentence that says which ones and why anything happened at all.
       */
      formErrors.unshift(
        fieldSummary(
          entries.map((entry) => entry.name),
          labels,
        ),
      );
    }

    return { ok: false, formErrors };
  };

/**
 * Maps the API's 422 `details` back onto the form.
 *
 * The server's issue paths are prefixed with their source (`body.email`), so the prefix
 * comes off and the rest is already a field path. Anything not under `body` — a bad
 * query or path parameter — is a bug rather than a user error, and surfaces at form
 * level rather than being pinned on an innocent input.
 */
export const applyServerIssues = (form: FormInstance, details: unknown): string[] => {
  if (!Array.isArray(details)) return [];

  const formErrors: string[] = [];
  const fields: { name: FieldPath; errors: string[] }[] = [];

  for (const raw of details) {
    if (typeof raw !== 'object' || raw === null) continue;
    const issue = raw as { path?: unknown; message?: unknown };
    if (typeof issue.path !== 'string' || typeof issue.message !== 'string') continue;

    const segments = issue.path.split('.');
    if (segments[0] !== 'body' || segments.length < 2) {
      formErrors.push(issue.message);
      continue;
    }
    // Numeric segments become numbers, so array paths line up with Form.List names.
    const name = segments.slice(1).map((segment) => (/^\d+$/.test(segment) ? Number(segment) : segment));
    fields.push({ name, errors: [issue.message] });
  }

  if (fields.length > 0) form.setFields(fields);
  return formErrors;
};

/**
 * The ergonomic wrapper every form uses.
 *
 * ```tsx
 * const { form, field, submit, formErrors } = useZodForm(createOfficerSchema);
 * <Form.Item {...field('name')} label="Name"><Input /></Form.Item>
 * ```
 *
 * `field()` spreads `name`, `rules` and `required` — so no component ever writes a rule.
 */
export const useZodForm = <T>(schema: ZodType<T>, options?: { labels?: FieldLabels }) => {
  const [form] = Form.useForm();
  const [formErrors, setFormErrors] = useState<string[]>([]);

  /**
   * `name` is ALWAYS returned as an array, even for a single segment.
   *
   * A `string | (string|number)[]` union makes antd's `Form.Item` infer its generic from
   * the union and reject both arms. An array is unambiguous, and it is what rc-field-form
   * normalises to internally anyway.
   */
  const field = useMemo(
    () =>
      (name: FieldPath | string): { name: (string | number)[]; rules: FormRule[]; required: boolean } => ({
        name: [...toPath(name)],
        rules: [zodRule(schema, name)],
        required: isFieldRequired(schema, name),
      }),
    [schema],
  );

  const labels = options?.labels;
  const submit = useMemo(() => {
    const gate = zodSubmit(schema, form, labels);
    return (raw: unknown): SubmitResult<T> => {
      const result = gate(raw);
      setFormErrors(result.ok ? [] : result.formErrors);
      return result;
    };
  }, [schema, form, labels]);

  return { form, field, submit, formErrors, setFormErrors, applyServerIssues } as const;
};
